import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import usePermissions from '../../../hooks/usePermissions';
import { Search as SearchIcon } from '@mui/icons-material';
import {
  Button, Tooltip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  TextField, InputAdornment, Fade,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import PettyCashModal from './PettyCashModal';
import Pagination from '../../common/Pagination';
import './PettyCashIndex.css';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const PettyCashIndex = () => {
  useSessionValidation();

  const { companyId, branchId, token, userId } = useAuth();
  const { canCreate, canEdit, canDelete } = usePermissions('Accounts');

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [initialData, setInitialData] = useState(null);

  // Data states
  const [pettyCashList, setPettyCashList] = useState([]);
  const [accountHeads, setAccountHeads] = useState([]);
  const [companyData, setCompanyData] = useState(null);

  // Search / filter / date states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [mounted, setMounted] = useState(false);

  const formatDateToYYYYMMDD = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Loading / error / success
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete / Activate dialogs
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    if (companyId && token) {
      fetchDropdownData();
      fetchCompanyData();
    }
  }, [companyId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchPettyCashList();
    }
  }, [companyId, token, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch helpers ──────────────────────────────────────────────────────────

  const fetchPettyCashList = async () => {
    setLoading(true);
    try {
      const activeRes = await fetchData('PettyCashApi', `/PettyCash/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeRes.response_data || activeRes || []).map(item => ({ ...item, IsActive: item.IsActive ?? true }));

      let inactiveData = [];
      try {
        const inactiveRes = await fetchData('PettyCashApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveRes.response_data || inactiveRes || []).map(item => ({ ...item, IsActive: false }));
      } catch { /* silent */ }

      setPettyCashList([...activeData, ...inactiveData]);
    } catch (err) {
      console.error('Error fetching petty cash list:', err);
      setPettyCashList([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const res = await fetchData('AccHeadApi', `/${companyId}`, token);
      setAccountHeads(res.response_data || res || []);
    } catch (err) {
      console.error('Error fetching account heads:', err);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const res = await fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token);
      setCompanyData(res.response_data || res || null);
    } catch { /* silent */ }
  };

  const fetchMaxBillNo = useCallback(async () => {
    try {
      const res = await fetchData('PettyCashApi', `/MaxBillno/${companyId}`, token);
      const data = res.response_data || res || {};
      return { billNumber: data.BillNo || data.billNo || 1 };
    } catch {
      return { billNumber: 1 };
    }
  }, [branchId, companyId, token]);


  // ── Modal handlers ─────────────────────────────────────────────────────────

  const handleOpenModal = useCallback(async (action, item = null) => {
    setModalAction(action);
    setSelectedItem(item);

    if (action === 'create') {
      setInitialData(null);
    } else if (action === 'edit' || action === 'view') {
      setModalLoading(true);
      try {
        const res = await fetchData('PettyCashApi', `/${item.Id}`, token);
        const data = res.response_data || res;
        const record = data.PettyCash || data;
        setInitialData({
          ...record,
          BillDate: record.BillDate ? record.BillDate.split('T')[0] : '',
          IsTypeReceipt: record.IsTypeReceipt ?? false,
        });
      } catch {
        setInitialData({
          ...item,
          BillDate: item.BillDate ? item.BillDate.split('T')[0] : '',
          IsTypeReceipt: item.IsTypeReceipt ?? false,
        });
      }
      setModalLoading(false);
    }
    setOpenModal(true);
  }, [token]);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setSelectedItem(null);
    setInitialData(null);
  }, []);

  const handleSave = useCallback(async (formData) => {
    setModalLoading(true);
    try {
      const payload = {
        Id: modalAction === 'edit' ? (formData.Id || 0) : 0,
        Companyid: companyId,
        FinYear: formData.FinYear || '',
        BillNo: parseInt(formData.BillNo) || 0,
        BillDate: formData.BillDate,
        IsTypeReceipt: formData.IsTypeReceipt ?? false,
        AccountHeadId: formData.AccountHeadId || null,
        Amount: parseFloat(formData.Amount) || 0,
        Remarks: formData.Remarks || null,
        IsActive: true,
        CreatedBy: modalAction === 'create' ? userId : (formData.CreatedBy || userId),
        CreatedDate: modalAction === 'create'
          ? new Date().toISOString().split('T')[0]
          : (formData.CreatedDate || new Date().toISOString().split('T')[0]),
        ModifiedBy: modalAction === 'edit' ? userId : null,
        ModifiedDate: modalAction === 'edit' ? new Date().toISOString().split('T')[0] : null,
      };

      if (modalAction === 'create') {
        await postData('PettyCashApi', '', payload, token);
        setSuccess('Petty cash entry created successfully!');
      } else {
        await updateData('PettyCashApi', formData.Id, payload, token);
        setSuccess('Petty cash entry updated successfully!');
      }

      handleCloseModal();
      await fetchPettyCashList();
    } catch (err) {
      console.error('Error saving petty cash:', err);
      setError('Failed to save petty cash entry.');
    }
    setModalLoading(false);
  }, [companyId, userId, modalAction, handleCloseModal, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    try {
      await deleteData('PettyCashApi', deleteItemId, token);
      setSuccess('Petty cash entry deleted successfully!');
      setDeleteModalOpen(false);
      await fetchPettyCashList();
    } catch {
      setError('Failed to delete petty cash entry.');
    }
  };

  const handleActivate = async () => {
    try {
      await activateData('PettyCashApi', activateItemId, token);
      setSuccess('Petty cash entry activated successfully!');
      setActivateModalOpen(false);
      await fetchPettyCashList();
    } catch {
      setError('Failed to activate petty cash entry.');
    }
  };


  // ── Number to words helper ─────────────────────────────────────────────────
  const numberToWords = (num) => {
    if (!num || num === 0) return 'Zero Rupees Only';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
      'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const toWords = (n) => {
      if (n === 0) return '';
      if (n < 20) return ones[n] + ' ';
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '') + ' ';
      if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred ' + toWords(n % 100);
      if (n < 100000) return toWords(Math.floor(n / 1000)) + 'Thousand ' + toWords(n % 1000);
      if (n < 10000000) return toWords(Math.floor(n / 100000)) + 'Lakh ' + toWords(n % 100000);
      return toWords(Math.floor(n / 10000000)) + 'Crore ' + toWords(n % 10000000);
    };
    const rupees = Math.floor(num);
    const paise = Math.round((num - rupees) * 100);
    let result = toWords(rupees).trim() + ' Rupees';
    if (paise > 0) result += ' and ' + toWords(paise).trim() + ' Paise';
    return result + ' Only';
  };

  // ── Print / PDF ────────────────────────────────────────────────────────────

  const handlePrint = useCallback(async (id) => {
    setLoading(true);
    try {
      const res = await fetchData('PettyCashApi', `/${id}`, token);
      const raw = res.response_data || res;
      const record = raw.PettyCash || raw;

      const accountHead = accountHeads.find(a => a.Id === record.AccountHeadId);
      const accountHeadName = record.AccountHeadName || accountHead?.Name || '-';
      const companyNameDisplay = companyData?.Name || 'Company Name';
      const companyAddress = companyData?.Address || '';
      const companyPhone = companyData?.Phone || companyData?.Mobile || '';
      const companyEmail = companyData?.Email || '';
      const isReceipt = record.IsTypeReceipt ?? false;
      const entryType = isReceipt ? 'RECEIPT' : 'PAYMENT';
      const typeColor = isReceipt ? [46, 125, 50] : [198, 40, 40];

      // Use same constructor style as billPdfGenerator.js
      const pdf = new jsPDF('l', 'mm', 'a5');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 12;
      const contentW = pageWidth - margin * 2;

      const brown = [92, 61, 30];
      const gold = [201, 168, 76];
      const lightGold = [253, 246, 236];
      const white = [255, 255, 255];
      const darkText = [30, 30, 30];
      const mutedText = [100, 100, 100];

      // ── Outer border ──────────────────────────────────────────────────────
      pdf.setDrawColor(gold[0], gold[1], gold[2]);
      pdf.setLineWidth(1.2);
      pdf.rect(6, 6, pageWidth - 12, pageHeight - 12, 'S');
      pdf.setLineWidth(0.3);
      pdf.rect(8, 8, pageWidth - 16, pageHeight - 16, 'S');

      let y = margin;

      // ── Company header band ───────────────────────────────────────────────
      pdf.setFillColor(brown[0], brown[1], brown[2]);
      pdf.rect(margin, y, contentW, 20, 'F');
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(white[0], white[1], white[2]);
      pdf.text(companyNameDisplay.toUpperCase(), pageWidth / 2, y + 8, { align: 'center' });
      const subLines = [companyAddress, companyPhone, companyEmail].filter(Boolean).join('  |  ');
      if (subLines) {
        pdf.setFontSize(7.5);
        pdf.setFont('helvetica', 'normal');
        pdf.text(subLines, pageWidth / 2, y + 15, { align: 'center' });
      }
      y += 24;

      // ── Voucher type banner ───────────────────────────────────────────────
      pdf.setFillColor(typeColor[0], typeColor[1], typeColor[2]);
      pdf.rect(margin, y, contentW, 9, 'F');
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(white[0], white[1], white[2]);
      pdf.text(`PETTY CASH ${entryType} VOUCHER`, pageWidth / 2, y + 6.5, { align: 'center' });
      y += 13;

      // ── Bill No + Date row ────────────────────────────────────────────────
      pdf.setFillColor(lightGold[0], lightGold[1], lightGold[2]);
      pdf.rect(margin, y, contentW, 8, 'F');
      pdf.setDrawColor(gold[0], gold[1], gold[2]);
      pdf.setLineWidth(0.3);
      pdf.rect(margin, y, contentW, 8, 'S');
      pdf.setFontSize(8.5);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(brown[0], brown[1], brown[2]);
      pdf.text(`Bill No: ${record.BillNo || '-'}`, margin + 4, y + 5.5);
      pdf.text(`Date: ${formatDate(record.BillDate)}`, pageWidth - margin - 4, y + 5.5, { align: 'right' });
      y += 12;

      // ── Main details table (jspdf-autotable v5: autoTable(doc, options)) ──
      const tableResult = autoTable(pdf, {
        startY: y,
        head: [['Field', 'Details']],
        body: [
          ['Account Head', accountHeadName],
          ['Amount', formatCurrency(record.Amount)],
          ['Remarks', record.Remarks || '-'],
          ['Financial Year', record.FinYear || '-'],
        ],
        theme: 'grid',
        headStyles: {
          fillColor: brown,
          textColor: white,
          fontSize: 8.5,
          fontStyle: 'bold',
          cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
        },
        bodyStyles: {
          fontSize: 9,
          textColor: darkText,
          cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
        },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 42, fillColor: lightGold, textColor: brown },
          1: { cellWidth: contentW - 42 },
        },
        didParseCell: (data) => {
          if (data.row.index === 1 && data.column.index === 1) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fontSize = 10;
            data.cell.styles.textColor = typeColor;
          }
        },
        margin: { left: margin, right: margin },
        tableWidth: contentW,
      });

      // v5 returns the table object with finalY
      y = (tableResult && tableResult.finalY != null)
        ? tableResult.finalY + 10
        : y + 50;

      // ── Amount in words ───────────────────────────────────────────────────
      const amountInWords = numberToWords(parseFloat(record.Amount) || 0);
      pdf.setFillColor(248, 248, 248);
      pdf.setDrawColor(gold[0], gold[1], gold[2]);
      pdf.setLineWidth(0.3);
      pdf.rect(margin, y, contentW, 9, 'FD');
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'italic');
      pdf.setTextColor(mutedText[0], mutedText[1], mutedText[2]);
      pdf.text(`Amount in words: ${amountInWords}`, margin + 4, y + 6);
      y += 14;

      // ── Signature boxes ───────────────────────────────────────────────────
      const remaining = pageHeight - margin - y;
      const sigH = Math.max(16, Math.min(22, remaining - 4));
      const sigW = contentW / 3;
      ['PREPARED BY', 'CHECKED BY', 'AUTHORIZED BY'].forEach((label, i) => {
        const x = margin + i * sigW;
        pdf.setFillColor(lightGold[0], lightGold[1], lightGold[2]);
        pdf.setDrawColor(gold[0], gold[1], gold[2]);
        pdf.setLineWidth(0.3);
        pdf.rect(x, y, sigW, sigH, 'FD');
        pdf.setDrawColor(brown[0], brown[1], brown[2]);
        pdf.setLineWidth(0.4);
        pdf.line(x + 4, y + sigH - 7, x + sigW - 4, y + sigH - 7);
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(brown[0], brown[1], brown[2]);
        pdf.text(label, x + sigW / 2, y + sigH - 2, { align: 'center' });
      });

      // ── Footer ────────────────────────────────────────────────────────────
      pdf.setFontSize(6.5);
      pdf.setFont('helvetica', 'italic');
      pdf.setTextColor(mutedText[0], mutedText[1], mutedText[2]);
      pdf.text(
        `Generated on ${new Date().toLocaleString('en-IN')}  |  This is a computer generated voucher`,
        pageWidth / 2, pageHeight - 5, { align: 'center' }
      );

      // ── Output — same pattern as billPdfGenerator.js ─────────────────────
      const blobUrl = pdf.output('bloburl');
      const win = window.open(blobUrl, '_blank');
      if (win) {
        win.addEventListener('load', () => { win.focus(); win.print(); });
      } else {
        pdf.save(`PettyCash_${entryType}_${record.BillNo}.pdf`);
      }
      setSuccess(`PDF generated for Bill No ${record.BillNo}`);
    } catch (err) {
      console.error('PDF generation error:', err);
      setError(`Failed to generate PDF: ${err?.message || err}`);
    }
    setLoading(false);
  }, [token, accountHeads, companyData, numberToWords]); // eslint-disable-line react-hooks/exhaustive-deps


  // ── Helpers ────────────────────────────────────────────────────────────────

  const formatCurrency = (amount) =>
    `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getAccountHeadName = (id) => {
    const ah = accountHeads.find(a => a.Id === id);
    return ah ? ah.Name : '-';
  };

  // ── Filter / Pagination ────────────────────────────────────────────────────

  const filteredData = useMemo(() => pettyCashList.filter(item => {
    const matchesSearch = !searchTerm ||
      item.BillNo?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.AccountHeadName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.Remarks?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && item.IsActive) ||
      (filterStatus === 'inactive' && !item.IsActive);

    return matchesSearch && matchesStatus;
  }), [pettyCashList, searchTerm, filterStatus]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = useMemo(
    () => filteredData.slice(startIndex, startIndex + rowsPerPage),
    [filteredData, startIndex, rowsPerPage]
  );

  // Totals split by type
  const totals = useMemo(() => {
    const active = filteredData.filter(i => i.IsActive);
    return {
      receipts: active.filter(i => i.IsTypeReceipt).reduce((s, i) => s + (parseFloat(i.Amount) || 0), 0),
      payments: active.filter(i => !i.IsTypeReceipt).reduce((s, i) => s + (parseFloat(i.Amount) || 0), 0),
    };
  }, [filteredData]);

  if (!mounted) return null;


  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ minWidth: '100%', background: '#fdf6ec', padding: '10px', position: 'relative' }}>
        {loading && <LogoLoader open={loading} message="Loading Petty Cash..." />}

        <div style={{
          maxWidth: '1400px', margin: '0 auto', background: 'white',
          borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          padding: '12px', color: 'black', fontFamily: 'Times New Roman',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0',
            flexWrap: 'wrap', gap: '6px',
          }}>
            <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Roman', textTransform: 'uppercase' }}>
              <span style={{ color: 'black' }}>💰 Petty Cash</span>
            </h4>
            {filteredData.length > 0 && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '12px', color: '#2e7d32', fontWeight: '600', background: '#e8f5e9', padding: '3px 10px', borderRadius: '6px', border: '1px solid #a5d6a7' }}>
                  ↓ Receipts: {formatCurrency(totals.receipts)}
                </span>
                <span style={{ fontSize: '12px', color: '#c62828', fontWeight: '600', background: '#fce4ec', padding: '3px 10px', borderRadius: '6px', border: '1px solid #f48fb1' }}>
                  ↑ Payments: {formatCurrency(totals.payments)}
                </span>
                <span style={{ fontSize: '12px', color: '#5c3d1e', fontWeight: '600', background: '#fdf6ec', padding: '3px 10px', borderRadius: '6px', border: '1px solid #e8d5b0' }}>
                  Balance: {formatCurrency(totals.receipts - totals.payments)}
                </span>
              </div>
            )}
          </div>

          {/* Filters Row */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
              <TextField
                type="date" size="small" value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                sx={{ width: '140px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '11px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
              <TextField
                type="date" size="small" value={toDate}
                onChange={e => setToDate(e.target.value)}
                sx={{ width: '140px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '11px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }}
              />
            </div>
            <TextField
              fullWidth size="small"
              placeholder="Search by bill no, account head or remarks..."
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: '#c9a84c', fontSize: '20px' }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: '200px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '13px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }}
            />
            <Tooltip title="Create a new petty cash entry" arrow>
              {canCreate && (
              <Button
                variant="contained"
                onClick={() => handleOpenModal('create')}
                style={{
                  background: 'linear-gradient(135deg, #5c3d1e 0%, #5c3d1e 100%)',
                  color: 'white', padding: '4px 12px', borderRadius: '6px',
                  fontSize: '12px', fontWeight: '700', border: 'none',
                  minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap',
                  boxShadow: '0 2px 8px rgba(92,61,30,0.4)',
                  display: 'flex', alignItems: 'center', gap: '4px',
                }}
              >
                <span style={{ fontSize: '14px' }}>+</span> Create
              </Button>
              )}
            </Tooltip>
          </div>


          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
              <Table sx={{ minWidth: 600 }} aria-label="petty cash table">
                <TableHead>
                  <TableRow style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #5c3d1e 100%)', height: '27px' }}>
                    {['Bill No', 'Date', 'Type', 'Account Head', 'Amount', 'Status', 'Actions'].map((h, i) => (
                      <TableCell key={i} style={{
                        color: 'white', fontWeight: '600', fontSize: '11px',
                        textTransform: 'uppercase', letterSpacing: '0.5px',
                        padding: '4px 12px', height: '27px',
                        textAlign: i === 6 ? 'right' : 'left',
                      }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '13px' }}>
                        No petty cash entries found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((item, index) => (
                      <TableRow key={item.Id} style={{ background: index % 2 === 0 ? '#ffffff' : '#fdf6ec', borderBottom: '2px solid #f0f0f0' }}>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: '600' }}>
                          {item.BillNo}
                          {item.FinYear && <div style={{ fontSize: '10px', color: '#999', fontWeight: '400' }}>FY: {item.FinYear}</div>}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                          {formatDate(item.BillDate)}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                          <span style={{
                            fontSize: '10px', fontWeight: '600', padding: '2px 8px', borderRadius: '4px',
                            background: item.IsTypeReceipt ? '#e8f5e9' : '#fce4ec',
                            color: item.IsTypeReceipt ? '#2e7d32' : '#c62828',
                          }}>
                            {item.IsTypeReceipt ? '↓ Receipt' : '↑ Payment'}
                          </span>
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: '500' }}>
                          {item.AccountHeadName || getAccountHeadName(item.AccountHeadId)}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', fontWeight: '600', color: item.IsTypeReceipt ? '#2e7d32' : '#c62828' }}>
                          {formatCurrency(item.Amount)}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                          <span style={{
                            fontSize: '10px', fontWeight: '600', padding: '1px 6px', borderRadius: '4px',
                            background: item.IsActive ? '#e8f5e9' : '#fce4ec',
                            color: item.IsActive ? '#2e7d32' : '#c62828',
                          }}>
                            {item.IsActive ? 'Active' : 'Inactive'}
                          </span>
                        </TableCell>
                        <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Tooltip title="View Details" arrow>
                              <Button size="small" onClick={() => handleOpenModal('view', item)}
                                style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600' }}>
                                👁️
                              </Button>
                            </Tooltip>
                            {/* <Tooltip title="Print / Download PDF" arrow>
                              <Button size="small" onClick={() => handlePrint(item.Id)}
                                style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600' }}>
                                🖨️
                              </Button>
                            </Tooltip> */}
                            {canEdit && (
                            <Tooltip title="Edit" arrow>
                              <Button size="small" onClick={() => handleOpenModal('edit', item)}
                                style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600' }}>
                                ✏️
                              </Button>
                            </Tooltip>
                            )}
                            {canDelete && (
                            <Tooltip title={item.IsActive ? 'Delete' : 'Activate'} arrow>
                              <Button size="small"
                                onClick={() => {
                                  if (item.IsActive) {
                                    setDeleteItemId(item.Id); setDeleteItemNo(item.BillNo); setDeleteModalOpen(true);
                                  } else {
                                    setActivateItemId(item.Id); setActivateItemNo(item.BillNo); setActivateModalOpen(true);
                                  }
                                }}
                                style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600' }}>
                                {item.IsActive ? '🗑️' : '✅'}
                              </Button>
                            </Tooltip>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredData.length}
            itemsPerPage={rowsPerPage}
            onPageChange={page => setCurrentPage(page)}
            onItemsPerPageChange={newSize => { setRowsPerPage(newSize); setCurrentPage(1); }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>


        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}>
          <DialogTitle style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>Delete Entry?</DialogTitle>
          <DialogContent>
            <DialogContentText style={{ fontSize: '13px', color: '#666' }}>
              Are you sure you want to delete Bill No {deleteItemNo}? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button onClick={() => setDeleteModalOpen(false)}
              style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', minHeight: '26px' }}>
              <span>❌</span> Cancel
            </Button>
            <Button onClick={handleDelete}
              style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', minHeight: '26px' }}>
              <span>✅</span> Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Activate Confirmation Dialog */}
        <Dialog open={activateModalOpen} onClose={() => setActivateModalOpen(false)} PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}>
          <DialogTitle style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>Activate Entry?</DialogTitle>
          <DialogContent>
            <DialogContentText style={{ fontSize: '13px', color: '#666' }}>
              Are you sure you want to activate Bill No {activateItemNo}?
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button onClick={() => setActivateModalOpen(false)}
              style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', minHeight: '26px' }}>
              <span>❌</span> Cancel
            </Button>
            <Button onClick={handleActivate}
              style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', minHeight: '26px' }}>
              <span>✅</span> Confirm Activate
            </Button>
          </DialogActions>
        </Dialog>

        {/* Petty Cash Modal */}
        <PettyCashModal
          open={openModal}
          onClose={handleCloseModal}
          onSave={handleSave}
          modalAction={modalAction}
          initialData={initialData}
          accountHeads={accountHeads}
          saving={modalLoading}
          fetchMaxBillNo={fetchMaxBillNo}
          onPrint={handlePrint}
        />

        {/* Snackbars */}
        <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>{success}</Alert>
        </Snackbar>
        <Snackbar open={!!error} autoHideDuration={3000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>{error}</Alert>
        </Snackbar>
      </div>
    </Fade>
  );
};

export default PettyCashIndex;
